import { useCallback, useEffect, useMemo } from "react"
import { useClient, useQuery } from "urql"

import { GetGoodsReceiptPalletsListDocument, type GoodsReceiptPallet } from "@/graphql/codegen/graphql"

interface ReturnProps {
  pallets: GoodsReceiptPallet[]
  isFetching: boolean
  error: Error | undefined
  refetch: () => Promise<GoodsReceiptPallet[]>
}

interface UseGetGoodsReceiptPalletsOptions {
  isPollingEnabled?: boolean
  pollInterval?: number
}

export const useGetGoodsReceiptPallets = (
  goodsReceiptId?: string | null,
  options: UseGetGoodsReceiptPalletsOptions = {}
): ReturnProps => {
  const { isPollingEnabled = false, pollInterval = 30000 } = options
  const client = useClient()
  const [{ data, fetching, error }, reexecuteQuery] = useQuery({
    query: GetGoodsReceiptPalletsListDocument,
    variables: {
      input: {
        filter: { goodsReceiptId },
        limit: 10000,
        offset: 0,
      },
    },
    pause: !goodsReceiptId,
    requestPolicy: isPollingEnabled ? "network-only" : "cache-first",
  })

  const refetch = useCallback(async () => {
    if (!goodsReceiptId) return []

    const result = await client
      .query(GetGoodsReceiptPalletsListDocument, {
        input: {
          filter: { goodsReceiptId },
          limit: 10000,
          offset: 0,
        },
      })
      .toPromise()

    if (result.data?.goodsReceipt.palletList.__typename === "GoodsReceiptPalletListSuccess") {
      // Also trigger UI update
      reexecuteQuery({ requestPolicy: "network-only" })
      return result.data.goodsReceipt.palletList.goodsReceiptPallets as GoodsReceiptPallet[]
    }

    return []
  }, [client, goodsReceiptId, reexecuteQuery])

  // Implement polling manually
  useEffect(() => {
    if (!isPollingEnabled || !goodsReceiptId) return

    const intervalId = setInterval(() => {
      reexecuteQuery({ requestPolicy: "network-only" })
    }, pollInterval)

    return () => clearInterval(intervalId)
  }, [isPollingEnabled, pollInterval, reexecuteQuery, goodsReceiptId])

  const pallets = useMemo<GoodsReceiptPallet[]>(() => {
    if (data?.goodsReceipt.palletList.__typename !== "GoodsReceiptPalletListSuccess") {
      return []
    }

    return data.goodsReceipt.palletList.goodsReceiptPallets as GoodsReceiptPallet[]
  }, [data])

  return {
    pallets,
    isFetching: fetching,
    error,
    refetch,
  }
}
